import React from 'react';
import { Col, Row, Progress } from 'antd';
import { Title, Body, Colors } from '@cognite/cogs.js';
import { Icon } from 'components/Icon';
import styled from 'styled-components/macro';
import { formatDateRecentTime } from 'utils/misc';
import { getValidInstancesPercentage } from 'utils/data-validation';
import {
  getDiffArrowDirection,
  getResultColorFromPercentage,
} from 'utils/runStatistics';
import { RuleRunStatus } from 'types/dt-graphql-apollo-v2';
import { DownloadResultsAsJsonButton } from 'components/DownloadResultsAsJsonButton';
import { LabelListDisplay } from 'components';
import { RunStatus } from 'types/graphql-apollo-v2';

export const ViewRunDetails = ({
  selectedRun,
  pastRun,
}: {
  selectedRun: RuleRunStatus;
  pastRun: RuleRunStatus | null;
}) => {
  const selectedRunPercentage = getValidInstancesPercentage(
    selectedRun?.totalValid || 0,
    selectedRun?.totalFailed || 0
  );
  const pastRunPercentage = getValidInstancesPercentage(
    pastRun?.totalValid || 0,
    pastRun?.totalFailed || 0
  );

  const getResultColorFromValue = (percentageDiff: number) => {
    if (percentageDiff > 0) return 'positive';
    if (percentageDiff < 0) return 'negative';
    return 'none';
  };

  const renderDifferenceFromLastValidation = () => {
    const percentageDiff = pastRun
      ? selectedRunPercentage - pastRunPercentage
      : 0;
    const percentageDiffValue =
      percentageDiff > 0
        ? `+${percentageDiff.toFixed(2)}%`
        : `${percentageDiff.toFixed(2)}%`;
    const resultColor = getResultColorFromValue(percentageDiff);
    return (
      <StyledResultDiffRow align="middle">
        <Title className={`diff-growth-${resultColor}`} level={6}>
          {percentageDiffValue}
        </Title>
        <Body level={6}>since last validation</Body>
        <Icon
          className={`diff-growth-${resultColor}`}
          type={getDiffArrowDirection(percentageDiff)}
        />
      </StyledResultDiffRow>
    );
  };

  const renderRunResult = () => {
    const resultColor = getResultColorFromPercentage(selectedRunPercentage);
    return (
      <StyledResultContainer align="middle">
        <Progress
          width={100}
          strokeColor={resultColor}
          strokeLinecap="square"
          type="circle"
          percent={selectedRunPercentage}
          format={(percent) => percent && `${percent.toPrecision(3)}`}
        />
        <Col>
          <StyledRow align="middle">
            <Body>Total valid instances:</Body>
            <Title level={6}>{selectedRun.totalValid}</Title>
          </StyledRow>
          <StyledRow align="middle">
            <Body>Total failed instances:</Body>
            <Title level={6}>{selectedRun.totalFailed}</Title>
          </StyledRow>
          {pastRun && renderDifferenceFromLastValidation()}
        </Col>
      </StyledResultContainer>
    );
  };

  return (
    <>
      <DetailsView>
        <RuleInformation>
          <StyledColumn>
            <Row>
              <StyledRunAt>
                {selectedRun?.runAt && formatDateRecentTime(selectedRun?.runAt)}
              </StyledRunAt>
            </Row>

            <Col>
              <Row justify="space-between">
                <Title level={3}>
                  {selectedRun?.rule?.name}
                  {selectedRun?.rule?.customerDefinedId &&
                    ` - ${selectedRun?.rule?.customerDefinedId}`}
                </Title>
              </Row>
            </Col>

            <LabelListDisplay
              labels={selectedRun.rule?.tags?.map((tag) => ({
                text: tag,
                key: tag,
              }))}
              labelSize="medium"
              limitLabels={5}
            />

            <Col>
              <Title level={6}>Message</Title>
              <Body level={6}>{selectedRun?.rule?.errorMessage || '-'}</Body>
            </Col>
          </StyledColumn>
        </RuleInformation>

        <RunDetails>
          <StyledColumn>
            {selectedRun.status === RunStatus.Success && (
              <>
                <Col>
                  <Title level={4}>Result</Title>
                  <StyledRow align="middle">
                    <Body level={6}>Total filtered instances</Body>
                    <Title level={6}>
                      {(selectedRun?.totalValid || 0) +
                        (selectedRun?.totalFailed || 0)}
                    </Title>
                  </StyledRow>
                  {renderRunResult()}
                </Col>
                <Col>
                  <Body level={6}>Download result as .json</Body>
                  <DownloadResultsAsJsonButton
                    runId={selectedRun.externalId}
                    fileName="failed-instances"
                  />
                </Col>
              </>
            )}
            {selectedRun.status === RunStatus.Error && selectedRun.message && (
              <Col>
                <Title level={4}>Error</Title>
                <Body level={6}>{selectedRun.message}</Body>
              </Col>
            )}
          </StyledColumn>
        </RunDetails>
      </DetailsView>
    </>
  );
};

const DetailsView = styled.div`
  width: 25vw;
  height: 100vh;
  border-left: solid 1px var(--cogs-border-default);
  overflow: auto;
  transition: all 250ms linear;
  padding-bottom: 2rem;
`;

const RuleInformation = styled.div`
  margin: 1rem;
  border-bottom: solid 1px var(--cogs-border-default);
`;

const RunDetails = styled(Col)`
  margin: 1rem;
`;

const StyledRunAt = styled.div`
  font-weight: 500;
  text-transform: capitalize;
`;

const StyledColumn = styled(Col)`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem;
  line-height: 24px;
`;

const StyledRow = styled(Row)`
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
`;

const StyledResultContainer = styled(StyledRow)`
  margin: 1rem 0rem;
`;

const StyledResultDiffRow = styled(StyledRow)`
  .diff-growth-positive {
    color: var(--cogs-green-1);
  }
  .diff-growth-negative {
    color: var(--cogs-danger);
  }
  .diff-growth-none {
    color: var(--cogs-greyscale-grey8);
  }
`;
